import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  Badge,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";

import { Client, getClients } from "../../services/clientService";
import {
  getProfessionals,
  Professional,
} from "../../services/professionalService";
import { Unit } from "../../services/unitService";
import {
  create,
  getAll,
  WaitlistEntry,
  WaitlistShift,
} from "../../services/waitlistService";

const CATALOG_PAGE_SIZE = 100;
const LIST_PER_PAGE = 100;
const SHIFT_OPTIONS: WaitlistShift[] = ["morning", "afternoon", "evening", "any"];

type WaitlistTab = "list" | "new";

type WaitlistFormValues = {
  unit_id: string;
  client_id: string;
  professional_id: string;
  date: string;
  shift: WaitlistShift | "";
};

type WaitlistOffcanvasProps = {
  isOpen: boolean;
  onClose: () => void;
  defaultUnitId?: string;
  units: Unit[];
};

const todayIsoDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const emptyFormValues = (defaultUnitId?: string): WaitlistFormValues => ({
  unit_id: defaultUnitId ?? "",
  client_id: "",
  professional_id: "",
  date: todayIsoDate(),
  shift: "",
});

const formatWaitlistDate = (value: string, locale: string): string => {
  const normalized = value.includes("T") ? value.slice(0, 10) : value;

  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(
    new Date(`${normalized}T12:00:00`)
  );
};

const WaitlistOffcanvas = ({
  isOpen,
  onClose,
  defaultUnitId,
  units,
}: WaitlistOffcanvasProps) => {
  const { t, i18n } = useTranslation();

  const [activeTab, setActiveTab] = useState<WaitlistTab>("list");
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const waitlistSchema = useMemo(
    () =>
      Yup.object({
        unit_id: Yup.string().required(t("agenda.waitlist.validation.unitRequired")),
        client_id: Yup.string().required(t("agenda.waitlist.validation.clientRequired")),
        professional_id: Yup.string().notRequired(),
        date: Yup.string().required(t("agenda.waitlist.validation.dateRequired")),
        shift: Yup.string()
          .required(t("agenda.waitlist.validation.shiftRequired"))
          .oneOf(SHIFT_OPTIONS, t("agenda.waitlist.validation.shiftInvalid")),
      }),
    [t]
  );

  const loadWaitlist = useCallback(async () => {
    setLoadingList(true);

    try {
      const response = await getAll({
        page: 1,
        per_page: LIST_PER_PAGE,
        unit_id: defaultUnitId ? Number(defaultUnitId) : undefined,
      });

      setEntries(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("agenda.waitlist.toast.loadError"));
      setEntries([]);
    } finally {
      setLoadingList(false);
    }
  }, [defaultUnitId, t]);

  const loadFormOptions = useCallback(async () => {
    setLoadingOptions(true);

    try {
      const [clientsResponse, professionalsResponse] = await Promise.all([
        getClients({ per_page: CATALOG_PAGE_SIZE }),
        getProfessionals({ per_page: CATALOG_PAGE_SIZE }),
      ]);

      setClients(clientsResponse.data);
      setProfessionals(professionalsResponse.data);
    } catch (error: any) {
      toast.error(error?.data || t("agenda.waitlist.toast.loadError"));
    } finally {
      setLoadingOptions(false);
    }
  }, [t]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    loadFormOptions();
    loadWaitlist();
  }, [isOpen, loadFormOptions, loadWaitlist]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab("list");
    }
  }, [isOpen]);

  const waitlistForm = useFormik<WaitlistFormValues>({
    initialValues: emptyFormValues(defaultUnitId),
    enableReinitialize: true,
    validationSchema: waitlistSchema,
    onSubmit: async (values, helpers) => {
      setSubmitting(true);

      try {
        const response = await create({
          unit_id: Number(values.unit_id),
          client_id: Number(values.client_id),
          professional_id: values.professional_id ? Number(values.professional_id) : null,
          date: values.date,
          shift: values.shift as WaitlistShift,
        });

        toast.success(response.message || t("agenda.waitlist.toast.createSuccess"));
        helpers.resetForm({ values: emptyFormValues(defaultUnitId) });
        setActiveTab("list");
        await loadWaitlist();
      } catch (error: any) {
        toast.error(error?.data || t("agenda.waitlist.toast.createError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const resolveShiftLabel = (shift: WaitlistShift): string =>
    t(`agenda.waitlist.shifts.${shift}`);

  const resolveProfessionalLabel = (entry: WaitlistEntry): string =>
    entry.professional?.name ?? t("agenda.waitlist.anyProfessional");

  return (
    <Offcanvas isOpen={isOpen} toggle={onClose} direction="end" className="border-0">
      <OffcanvasHeader toggle={onClose}>{t("agenda.waitlist.title")}</OffcanvasHeader>
      <OffcanvasBody>
        <Nav tabs className="nav-tabs-custom nav-success mb-3">
          <NavItem>
            <NavLink
              href="#"
              className={activeTab === "list" ? "active" : ""}
              onClick={(event) => {
                event.preventDefault();
                setActiveTab("list");
                loadWaitlist();
              }}
            >
              {t("agenda.waitlist.tabs.list")}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              href="#"
              className={activeTab === "new" ? "active" : ""}
              onClick={(event) => {
                event.preventDefault();
                setActiveTab("new");
              }}
            >
              {t("agenda.waitlist.tabs.new")}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="list">
            {loadingList ? (
              <div className="text-center py-4">
                <Spinner color="primary">{t("studio.agenda.loading")}</Spinner>
              </div>
            ) : entries.length === 0 ? (
              <p className="text-muted mb-0">{t("agenda.waitlist.empty")}</p>
            ) : (
              <ListGroup flush>
                {entries.map((entry) => (
                  <ListGroupItem key={entry.id} className="px-0">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <div className="fw-medium">
                          {entry.client?.name ?? t("agenda.waitlist.unknownClient")}
                        </div>
                        <small className="text-muted d-block">
                          {formatWaitlistDate(entry.date, i18n.language)}
                        </small>
                        <small className="text-muted d-block">
                          {resolveShiftLabel(entry.shift)}
                          {" · "}
                          {resolveProfessionalLabel(entry)}
                        </small>
                      </div>
                      <Badge color="warning" className="text-uppercase">
                        {t(`agenda.waitlist.status.${entry.status}`, {
                          defaultValue: entry.status,
                        })}
                      </Badge>
                    </div>
                  </ListGroupItem>
                ))}
              </ListGroup>
            )}
          </TabPane>

          <TabPane tabId="new">
            {loadingOptions ? (
              <div className="text-center py-4">
                <Spinner color="primary">{t("studio.agenda.loading")}</Spinner>
              </div>
            ) : (
              <Form onSubmit={waitlistForm.handleSubmit}>
                {!defaultUnitId && (
                  <div className="mb-3">
                    <Label htmlFor="waitlist-unit">{t("agenda.waitlist.fields.unit")}</Label>
                    <Input
                      id="waitlist-unit"
                      name="unit_id"
                      type="select"
                      value={waitlistForm.values.unit_id}
                      onChange={waitlistForm.handleChange}
                      onBlur={waitlistForm.handleBlur}
                      invalid={
                        !!(waitlistForm.touched.unit_id && waitlistForm.errors.unit_id)
                      }
                    >
                      <option value="">{t("agenda.waitlist.fields.unitPlaceholder")}</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </Input>
                    <FormFeedback>{waitlistForm.errors.unit_id}</FormFeedback>
                  </div>
                )}

                <div className="mb-3">
                  <Label htmlFor="waitlist-client">{t("agenda.waitlist.fields.client")}</Label>
                  <Input
                    id="waitlist-client"
                    name="client_id"
                    type="select"
                    value={waitlistForm.values.client_id}
                    onChange={waitlistForm.handleChange}
                    onBlur={waitlistForm.handleBlur}
                    invalid={
                      !!(waitlistForm.touched.client_id && waitlistForm.errors.client_id)
                    }
                  >
                    <option value="">{t("agenda.waitlist.fields.clientPlaceholder")}</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </Input>
                  <FormFeedback>{waitlistForm.errors.client_id}</FormFeedback>
                </div>

                <div className="mb-3">
                  <Label htmlFor="waitlist-professional">
                    {t("agenda.waitlist.fields.professional")}
                  </Label>
                  <Input
                    id="waitlist-professional"
                    name="professional_id"
                    type="select"
                    value={waitlistForm.values.professional_id}
                    onChange={waitlistForm.handleChange}
                    onBlur={waitlistForm.handleBlur}
                  >
                    <option value="">{t("agenda.waitlist.fields.professionalPlaceholder")}</option>
                    {professionals.map((professional) => (
                      <option key={professional.id} value={professional.id}>
                        {professional.name}
                      </option>
                    ))}
                  </Input>
                </div>

                <div className="mb-3">
                  <Label htmlFor="waitlist-date">{t("agenda.waitlist.fields.date")}</Label>
                  <Input
                    id="waitlist-date"
                    name="date"
                    type="date"
                    value={waitlistForm.values.date}
                    onChange={waitlistForm.handleChange}
                    onBlur={waitlistForm.handleBlur}
                    invalid={!!(waitlistForm.touched.date && waitlistForm.errors.date)}
                  />
                  <FormFeedback>{waitlistForm.errors.date}</FormFeedback>
                </div>

                <div className="mb-3">
                  <Label htmlFor="waitlist-shift">{t("agenda.waitlist.fields.shift")}</Label>
                  <Input
                    id="waitlist-shift"
                    name="shift"
                    type="select"
                    value={waitlistForm.values.shift}
                    onChange={waitlistForm.handleChange}
                    onBlur={waitlistForm.handleBlur}
                    invalid={!!(waitlistForm.touched.shift && waitlistForm.errors.shift)}
                  >
                    <option value="">{t("agenda.waitlist.fields.shiftPlaceholder")}</option>
                    {SHIFT_OPTIONS.map((shift) => (
                      <option key={shift} value={shift}>
                        {resolveShiftLabel(shift)}
                      </option>
                    ))}
                  </Input>
                  <FormFeedback>{waitlistForm.errors.shift}</FormFeedback>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <Button color="light" type="button" onClick={onClose}>
                    {t("studio.agenda.actions.cancel")}
                  </Button>
                  <Button color="primary" type="submit" disabled={submitting}>
                    {submitting ? t("studio.agenda.loading") : t("agenda.waitlist.actions.save")}
                  </Button>
                </div>
              </Form>
            )}
          </TabPane>
        </TabContent>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default WaitlistOffcanvas;
